import { useMemo } from "react";
import { Loading } from "@/components/atoms";
import { DataTable, Pagination } from "@/components/molecules";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { usePagination, useProfile } from "@/hooks";
import { useLazyGetMonthlyInvoiceItemQuery } from "@/redux/apis/payment/paymentApi";
import { useAppSelector } from "@/redux/hooks";
import { monthlyInvoicePageSelector } from "@/redux/slices/payment/selectors";
import { MonthlyInvoiceItem } from "@/redux/slices/payment/types";
import { addToast, clsx, downloadFile } from "@/utils";
import { generateColumns, generateData } from "./helpers";

interface MonthlyInvoiceTableProps extends React.HTMLAttributes<HTMLDivElement> {
  items: MonthlyInvoiceItem[];
}

export const MonthlyInvoiceTable = ({ items, className, ...props }: MonthlyInvoiceTableProps) => {
  const { dateFormat } = useProfile();
  const [getMonthlyInvoiceItem, { isFetching }] = useLazyGetMonthlyInvoiceItemQuery();

  const handleDownload = (id: string, documentType: string) => {
    getMonthlyInvoiceItem(id)
      .unwrap()
      .then((res) => {
        if (documentType === "pdf") downloadFile(res.documents.pdf, res.documents.pdf);
        else if (documentType === "csv") downloadFile(res.documents.csv, res.documents.csv);
        else addToast("danger", "Invalid document type");
      })
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  };

  const data = useMemo(
    () => generateData(items, dateFormat, handleDownload),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items]
  );
  const columns = useMemo(() => generateColumns(), []);

  return (
    <div className={clsx("relative flex bg-white", className)}>
      {isFetching && <Loading className="rounded-lg" />}
      <DataTable className="w-full overflow-x-auto [&_table]:table-auto" columns={columns} data={data} {...props} />
    </div>
  );
};

const MonthlyInvoiceTablePagination = () => {
  const { current, count, total, size } = useAppSelector(monthlyInvoicePageSelector);
  const { handlePageClick, handleSizeChange } = usePagination("monthly-invoice");

  return (
    <Pagination
      onPageClick={handlePageClick}
      onSizeChange={handleSizeChange}
      currentPage={current}
      pageSize={size}
      totalItems={total}
      pageCount={count}
    />
  );
};

MonthlyInvoiceTable.Pagination = MonthlyInvoiceTablePagination;
